/**
 * Pure decision logic for clicking a row in a project list, and for the
 * restore / delete dialog that a row of the archive opens.
 *
 * It lives outside the components on purpose: neither `SimpleProjectOverview`
 * nor `RestoreAndDeleteProjectsDialog` can be mounted in the unit tests (the
 * `@vue/test-utils` build available here is the Vue 3 one), so the branches
 * that decide whether a user is offered "Wiederherstellen" at all would
 * otherwise be untestable.
 */

/** The event a project row emits when it is clicked. */
export type ProjectRowClickEvent =
  | "openProjectDetails"
  | "restoreOrDeleteProject";

export interface ProjectRowClickInput {
  /** The list is picking projects for a bulk restore / delete. */
  isSelectionMode: boolean;
  /** The row belongs to the archive (an archived or a deleted project). */
  isArchivedProject: boolean;
  /** The viewer is a user of an administrator client, who only ever reads. */
  isAdministratorClientUser: boolean;
  /** The list already holds the maximum number of selected projects. */
  hasReachedMaximumSelectionLimit: boolean;
}

export interface ProjectRowClickOutcome {
  event: ProjectRowClickEvent;
  /**
   * Whether the row has to drop its own selection flag again, because the
   * list refuses the pick once the selection limit is reached.
   */
  clearsOwnSelection: boolean;
}

/**
 * What a click on a project row does. In selection mode the click always goes
 * to the list, which either takes the pick or refuses it; outside selection
 * mode a row of the archive offers restore / delete instead of opening the
 * project, unless the viewer belongs to an administrator client.
 */
export function resolveProjectRowClick(
  input: ProjectRowClickInput
): ProjectRowClickOutcome {
  if (input.isSelectionMode) {
    return {
      event: "openProjectDetails",
      clearsOwnSelection: input.hasReachedMaximumSelectionLimit,
    };
  }

  const offersRestoreOrDelete =
    input.isArchivedProject && !input.isAdministratorClientUser;

  return {
    event: offersRestoreOrDelete
      ? "restoreOrDeleteProject"
      : "openProjectDetails",
    clearsOwnSelection: false,
  };
}

/** What a button of the restore / delete dialog does. */
export enum RestoreAndDeleteAction {
  RESTORE = "RESTORE",
  DELETE = "DELETE",
  /** The button is not offered. */
  NONE = "NONE",
}

export interface RestoreAndDeleteAccess {
  canRestoreProject: boolean;
  canDeleteProject: boolean;
}

export interface RestoreAndDeleteButtons {
  /** The rightmost button of the dialog. */
  positive: RestoreAndDeleteAction;
  /** The button left of the positive one. */
  neutralPositive: RestoreAndDeleteAction;
}

/**
 * Which buttons the restore / delete dialog offers. Restoring takes the
 * positive slot when it is allowed, which pushes deleting into the neutral
 * positive slot; deleting never appears twice.
 */
export function resolveRestoreAndDeleteButtons(
  access: RestoreAndDeleteAccess
): RestoreAndDeleteButtons {
  const positive = access.canRestoreProject
    ? RestoreAndDeleteAction.RESTORE
    : access.canDeleteProject
    ? RestoreAndDeleteAction.DELETE
    : RestoreAndDeleteAction.NONE;

  const offersDeleteAlready = positive === RestoreAndDeleteAction.DELETE;

  return {
    positive,
    neutralPositive:
      access.canDeleteProject && !offersDeleteAlready
        ? RestoreAndDeleteAction.DELETE
        : RestoreAndDeleteAction.NONE,
  };
}

export interface RestoreAndDeleteTitleInput extends RestoreAndDeleteAccess {
  /** The project sits in the archive (archived or deleted). */
  isArchivedProject: boolean;
  /** The project is in the deleted-projects view, where deleting is final. */
  isDeletedProject: boolean;
}

/** The i18n key for the dialog's title, which takes the project name as `{0}`. */
export function resolveRestoreAndDeleteTitleKey(
  input: RestoreAndDeleteTitleInput
): string {
  const deletionTitleKey = input.isDeletedProject
    ? "archivedProjects.deletionConfirmationDialog.titleAlt"
    : "archivedProjects.deletionConfirmationDialog.title";

  if (input.canRestoreProject && input.canDeleteProject) {
    if (!input.isArchivedProject) {
      return deletionTitleKey;
    }

    return input.isDeletedProject
      ? "archivedProjects.restorationAndDeletionDialog.titleAlt"
      : "archivedProjects.restorationAndDeletionDialog.title";
  }

  if (input.canRestoreProject) {
    return "archivedProjects.restorationConfirmationDialog.title";
  }

  return deletionTitleKey;
}

/** The minimum an overview needs for `dedupeProjectsPreferringArchive` to place it. */
export interface DedupableProject {
  id: string;
  projectListType?: unknown;
}

/**
 * Collapses the searchable set to one row per project, keeping the archive's copy.
 *
 * The search looks across the archive *and* the running / finished lists, and the same project id
 * can be present in two overview collections at once. Measured at SanTec on 2026-09-08: 17 ids in
 * more than one collection, 5 of them involving the archive.
 *
 * Concatenating them produced two rows for such a project, which broke two things at once:
 *
 * - **Duplicate `:key` values** in the row `v-for`, so Vue patches rows against the wrong data and
 *   entries appear to go missing from the results.
 * - **The wrong row behaviour.** A row only offers "wiederherstellen / löschen" when its
 *   `projectListType` is an archive one, so the running / finished copy silently opens the project
 *   detail instead. That is the reported symptom: the restore pop-up never appears for exactly
 *   those projects, whoever is logged in.
 *
 * The archive copy wins because this list is the archive, and it is the copy that can be restored.
 * Fixing the duplicated data is a separate job; until then the list has to survive it.
 *
 * @param archived Rows from the archive, already tagged with an archive `projectListType`.
 * @param others Rows from the running / finished lists, used only by the search.
 */
export function dedupeProjectsPreferringArchive<T extends DedupableProject>(
  archived: T[],
  others: T[]
): T[] {
  const byId = new Map<string, T>();

  // Archive first, and later duplicates are refused, so the archive copy always wins.
  for (const project of [...archived, ...others]) {
    if (project.id && !byId.has(project.id)) {
      byId.set(project.id, project);
    }
  }

  return [...byId.values()];
}
